import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ExternalLink,
  Link2,
  Loader2,
  Pencil,
  Send,
} from 'lucide-react';
import QuestionReportService from '../../../sdk/services/QuestionReportService';
import QuestaoService from '../../../sdk/services/QuestaoService';
import type {
  ReportCategory,
  ReportDetail as ReportDetailData,
  ReportStatus,
} from '../../../sdk/@types/Forum';
import { readApiError } from '../../../sdk/errors/apiError';
import AccessDenied from '../AccessDenied/AccessDenied';
import {
  REPORT_CATEGORY_LABELS,
  initials,
  questionRefLine,
  relativeTime,
  studentCountLabel,
} from './reportLabels';

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(' ');

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

const BTN =
  'inline-flex h-9 items-center justify-center gap-2 rounded-input px-3 text-sm ' +
  'transition-colors duration-150 motion-reduce:transition-none disabled:cursor-not-allowed ' +
  'disabled:opacity-60 ' +
  FOCUS;

const BTN_NEUTRAL =
  'border border-edge bg-surface font-medium text-ink-secondary ' +
  'hover:border-edge-strong hover:bg-surface-hover hover:text-ink';

const BTN_PRIMARY = 'bg-brand font-semibold text-brand-contrast hover:brightness-110';

/** `danger` só aqui e no confirmar do modal — é a única ação destrutiva (§7). */
const BTN_DANGER =
  'border border-danger/50 bg-danger/[0.10] font-semibold text-danger-ink ' +
  'hover:bg-danger/[0.18]';

/**
 * "Concluir: corrigida" — uma decisão com peso, **sem** a família `danger`.
 *
 * Encerrar um caso cuja questão já foi corrigida não destrói nada: a questão
 * continua no banco, valendo para todos os alunos. O peso vem de
 * `border-edge-strong` + `bg-surface-muted` + `font-semibold`, não de cor
 * semântica — `ok` e `warn` são reservados aos seus próprios significados e não
 * servem de ênfase (§7/§54).
 */
const BTN_DECIDE =
  'border border-edge-strong bg-surface-muted font-semibold text-ink ' +
  'hover:bg-surface-hover';

const SECTION_TITLE =
  'text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted';

/**
 * Resumo de um desfecho, para o aviso que a **fila** mostra depois de mover o
 * grupo — quem sobrevive à troca de seleção é ela, não este detalhe.
 */
export interface ReportOutcomeInfo {
  questionId: string;
  questionNumber: string;
  /**
   * Quantos denunciantes a API disse ter notificado.
   *
   * **Opcional de propósito.** Quem sabe quantos alunos receberam o aviso é o
   * servidor, que os contou ao escrever as notificações; se a resposta não
   * trouxer a contagem, o número é desconhecido e o aviso é mostrado **sem
   * número** — nunca `NaN` nem `undefined` interpolados numa frase. Inventar um
   * número plausível (o total de denunciantes carregado na tela) diria ao
   * moderador algo que ninguém confirmou.
   */
  notified?: number;
  /**
   * - `deactivated`     — aceite: aviso com "Desfazer" (`undo-accept`).
   * - `resolved`        — concluída como corrigida: confirmação simples, sem
   *   desfazer (não há `undo-resolve`; nada foi feito na questão para reverter).
   * - `already-decided` — `404`/`409`: outro moderador decidiu antes. Aviso
   *   informativo, não erro.
   */
  kind: 'deactivated' | 'resolved' | 'already-decided';
}

export interface ReportDetailProps {
  questionId: string;
  /** Situação da sub-fila de onde o item veio — decide o que fica acionável. */
  status?: ReportStatus;
  /**
   * A denúncia mudou de estado: a fila move o grupo para a sub-fila `next` e
   * mostra o aviso descrito em `info`.
   */
  onResolved?: (next: ReportStatus, info?: ReportOutcomeInfo) => void;
  /** Só para teste/storybook: o padrão navega para o editor de questão. */
  onOpenEditor?: (detail: ReportDetailData) => void;
}

/** Moda das categorias das denúncias recebidas — o "motivo" do grupo. */
function predominantCategory(detail: ReportDetailData | null): ReportCategory | null {
  if (!detail || !detail.reports.length) return null;
  const tally = new Map<ReportCategory, number>();
  detail.reports.forEach((r) => tally.set(r.category, (tally.get(r.category) ?? 0) + 1));
  let winner: ReportCategory = detail.reports[0].category;
  let best = 0;
  tally.forEach((n, c) => {
    if (n > best) {
      best = n;
      winner = c;
    }
  });
  return winner;
}

/**
 * Detalhe de uma questão denunciada (frame `Pgime`).
 *
 * Mostra a questão como o aluno a vê — enunciado e alternativas com o gabarito
 * atual marcado —, como os alunos de fato responderam, e as denúncias
 * recebidas. Não edita gabarito: "Abrir no editor" e "Corrigir gabarito" levam
 * ao editor de questão existente (`/questao`), premissa 8 do proposal.
 */
export default function ReportDetail({
  questionId,
  status = 'open',
  onResolved,
  onOpenEditor,
}: ReportDetailProps) {
  const navigate = useNavigate();

  const [detail, setDetail] = useState<ReportDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ReturnType<typeof readApiError> | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState<'respond' | 'reject' | 'accept' | 'resolve' | null>(null);
  const [sent, setSent] = useState(false);
  /** Falha de servidor numa decisão — o grupo fica na fila e dá para repetir. */
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sendToContentTeam, setSendToContentTeam] = useState(true);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  /** Trava síncrona da conclusão — ver `resolve`. */
  const inFlight = useRef(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setDetail(null);
    setMessage('');
    setSent(false);
    setActionError(null);
    inFlight.current = false;
    QuestionReportService.getByQuestion(questionId)
      .then((data) => {
        if (alive) setDetail(data);
      })
      .catch((err) => {
        if (alive) setError(readApiError(err));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [questionId]);

  const questionNumber = String(detail?.questionRef.number ?? questionId);
  const category = predominantCategory(detail);

  const students = useMemo(() => {
    if (!detail) return 0;
    return new Set(detail.reports.map((r) => r.author.id || r.author.name)).size;
  }, [detail]);

  const answerKey = detail?.correctChoice || null;

  /** A alternativa mais marcada; só interessa quando difere do gabarito. */
  const topChoice = useMemo(() => {
    const list = detail?.answerStats.byChoice ?? [];
    if (!list.length) return null;
    return list.reduce((best, c) => (c.pct > best.pct ? c : best), list[0]);
  }, [detail]);

  const divergent = Boolean(
    answerKey && topChoice && topChoice.pct > 0 && topChoice.choice !== answerKey
  );

  /**
   * As decisões só aparecem enquanto o caso está aberto — e lemos as duas
   * fontes: a sub-fila de onde o item veio (`status`) e o estado do próprio
   * detalhe, que uma decisão desta sessão acaba de atualizar. Sem a segunda, o
   * botão continuaria clicável depois de concluir, até a fila trocar a seleção.
   */
  const decided =
    detail?.status === 'resolved' ||
    detail?.status === 'accepted' ||
    detail?.status === 'rejected';
  const canDecide = status === 'open' && !decided;

  const openEditor = useCallback(async () => {
    if (!detail) return;
    if (onOpenEditor) {
      onOpenEditor(detail);
      return;
    }
    // Mesmo contrato do QuestionList: o editor lê `location.state.questao` — a
    // questão completa, que o ReportDetail não carrega; busca antes de navegar.
    try {
      const questao = await QuestaoService.getQuestao(detail.questionId);
      navigate('/questao', {
        state: { questao, questaoId: detail.questionId },
      });
    } catch {
      // 401/403 já viram toast no interceptor.
    }
  }, [detail, navigate, onOpenEditor]);

  const respond = async () => {
    if (!message.trim() || busy) return;
    try {
      setBusy('respond');
      setActionError(null);
      await QuestionReportService.respond(questionId, message.trim());
      // `respond` notifica e **não decide**: o grupo continua em Abertas, e a
      // leitura depois do envio diz isso (decisão 6 do design).
      setSent(true);
    } catch {
      // 401/403 já viram toast no interceptor; o resto fica no estado do botão.
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    if (busy) return;
    try {
      setBusy('reject');
      setActionError(null);
      await QuestionReportService.reject(questionId);
      onResolved?.('rejected');
    } catch {
      /* idem */
    } finally {
      setBusy(null);
    }
  };

  /**
   * "Concluir: corrigida" — o desfecho `resolved`.
   *
   * Três coisas que esta função deliberadamente **não** faz, e cada ausência é
   * a feature:
   *
   * 1. Não chama `accept` nem nenhuma rota de desativação, e não mexe em
   *    `detail.disabled` — a questão foi corrigida, logo continua valendo para
   *    todos os alunos, e "Desativar questão" segue oferecida como opção à
   *    parte.
   * 2. Não abre modal de confirmação: não há efeito sobre o aluno a confirmar.
   * 3. Não **exige** o texto do campo de resposta: a mensagem é opcional, como
   *    em `accept` (Open Question do design). Escrita, ela vai no corpo do
   *    `resolve` e vira o corpo do aviso que cada denunciante recebe; vazia, o
   *    servidor usa o texto padrão dele. Por isso este botão nasce habilitado
   *    com o campo vazio, ao contrário de "Responder", que sem texto não teria
   *    o que enviar.
   *
   * `404`/`409` significam "não há denúncia aberta nesta questão": outro
   * moderador decidiu antes. É aviso informativo, e o grupo sai de Abertas.
   * `500` mantém o grupo na fila e mostra a falha, para tentar de novo.
   */
  const resolve = async () => {
    // O `busy` de estado desabilita o botão, mas só depois de a re-renderização
    // acontecer; dois cliques no mesmo tick leem o `busy` antigo e mandariam
    // dois POSTs. O ref fecha essa janela — é síncrono.
    if (busy || inFlight.current) return;
    inFlight.current = true;
    const note = message.trim();
    try {
      setBusy('resolve');
      setActionError(null);
      const result = await QuestionReportService.resolve(questionId, {
        notify: true,
        // Só vai o que existe: um `message: ''` seria um campo a mais dizendo
        // a mesma coisa que a ausência dele.
        ...(note ? { message: note } : {}),
      });
      setDetail((prev) => (prev ? { ...prev, status: 'resolved' } : prev));
      // O texto já cumpriu o papel — foi junto na conclusão. Deixá-lo no campo
      // convidaria a mandá-lo de novo por "Responder", notificando duas vezes.
      setMessage('');
      setSent(false);
      const notified = Number(result?.notified);
      onResolved?.('resolved', {
        kind: 'resolved',
        questionId,
        questionNumber,
        // Sem contagem na resposta, `notified` fica ausente e o aviso sai sem
        // número — em vez de "NaN alunos notificados".
        ...(Number.isFinite(notified) ? { notified } : {}),
      });
    } catch (err) {
      const { status: httpStatus } = readApiError(err);
      if (httpStatus === 404 || httpStatus === 409) {
        onResolved?.('resolved', {
          kind: 'already-decided',
          questionId,
          questionNumber,
        });
        return;
      }
      setActionError('Não foi possível concluir a denúncia. Tente de novo.');
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  };

  const accept = async () => {
    if (busy) return;
    try {
      setBusy('accept');
      setActionError(null);
      const result = await QuestionReportService.accept(questionId, {
        notify: true,
        sendToContentTeam,
      });
      setConfirmOpen(false);
      setDetail((prev) => (prev ? { ...prev, disabled: true, status: 'accepted' } : prev));
      onResolved?.('accepted', {
        kind: 'deactivated',
        questionId,
        questionNumber,
        notified: Number.isFinite(Number(result?.notified))
          ? Number(result.notified)
          : students,
      });
    } catch {
      setConfirmOpen(false);
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (!confirmOpen) return;
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirmOpen]);

  if (loading) {
    return (
      <div
        className="flex min-h-[320px] items-center justify-center rounded-card border border-edge bg-surface"
        role="status"
        aria-label="Carregando denúncia"
      >
        <Loader2 size={20} className="animate-spin text-ink-muted" />
      </div>
    );
  }

  if (error?.kind) {
    return <AccessDenied kind={error.kind} detail={error.raw} variant="panel" onNavigate={navigate} />;
  }

  if (!detail) {
    return (
      <div className="rounded-card border border-edge bg-surface p-6 text-sm text-ink-secondary">
        Não foi possível carregar esta denúncia.
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4" data-testid="report-detail">
      {/* ── Cabeçalho: identificação + atalho para o editor ─────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-card border border-edge bg-surface p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-ink">Questão #{questionNumber}</h2>
            <span
              className={cx(
                'rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                detail.disabled
                  ? 'border-warn/40 bg-warn/[0.12] text-warn-ink'
                  : 'border-ok/40 bg-ok/[0.12] text-ok-ink'
              )}
            >
              {detail.disabled ? 'Desativada' : 'Ativa'}
            </span>
          </div>
          <p className="mt-1 truncate text-xs text-ink-muted">
            {questionRefLine(detail.questionRef) || '—'}
          </p>
        </div>
        <button type="button" className={cx(BTN, BTN_NEUTRAL)} onClick={openEditor}>
          <Pencil size={15} />
          Abrir no editor
        </button>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        {/* ── Enunciado e alternativas, com o gabarito atual marcado ────── */}
        <section className="rounded-card border border-edge bg-surface p-4">
          <h3 className={SECTION_TITLE}>Enunciado</h3>
          <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-ink">
            {detail.statement}
          </p>

          <ul className="mt-4 flex flex-col gap-2">
            {detail.alternatives.map((alternative) => (
              <li
                key={alternative.choice}
                data-testid={`choice-${alternative.choice}`}
                className={cx(
                  'flex items-start gap-3 rounded-chip border px-3 py-2.5',
                  alternative.choice === detail.correctChoice
                    ? 'border-ok/40 bg-ok/[0.10]'
                    : 'border-edge bg-surface-muted'
                )}
              >
                <span
                  className={cx(
                    'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                    'border-[1.5px] text-[12px] font-semibold',
                    alternative.choice === detail.correctChoice
                      ? 'border-ok bg-ok text-white dark:text-canvas'
                      : 'border-edge-strong text-ink-secondary'
                  )}
                >
                  {alternative.choice}
                </span>
                <span className="min-w-0 flex-1 text-sm leading-snug text-ink-secondary">
                  {alternative.text}
                </span>
                {alternative.choice === detail.correctChoice && (
                  <span className="shrink-0 rounded-chip border border-ok/40 px-2 py-0.5 text-[11px] font-semibold text-ok-ink">
                    Gabarito atual
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>

        {/* ── Distribuição das respostas ────────────────────────────────── */}
        <section
          className="rounded-card border border-edge bg-surface p-4"
          data-testid="answer-distribution"
        >
          <h3 className={SECTION_TITLE}>Como os alunos responderam</h3>
          <p className="mt-2 flex items-baseline gap-1.5">
            <span className="text-2xl font-semibold text-ink">
              {detail.answerStats.total.toLocaleString('pt-BR')}
            </span>
            <span className="text-xs text-ink-muted">respostas</span>
          </p>

          <ul className="mt-3 flex flex-col gap-2">
            {detail.answerStats.byChoice.map((stat) => {
              const isKey = stat.choice === answerKey;
              const isTopOffKey = divergent && stat.choice === topChoice?.choice;
              return (
                <li
                  key={stat.choice}
                  className="flex items-center gap-2"
                  data-testid={`answer-stat-${stat.choice}`}
                >
                  <span
                    className={cx(
                      'w-4 shrink-0 text-xs font-semibold',
                      isKey ? 'text-ok-ink' : isTopOffKey ? 'text-warn-ink' : 'text-ink-secondary'
                    )}
                  >
                    {stat.choice}
                  </span>
                  <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-muted">
                    <span
                      className={cx(
                        'block h-full rounded-full',
                        isKey ? 'bg-ok' : isTopOffKey ? 'bg-warn' : 'bg-edge-strong'
                      )}
                      style={{ width: `${Math.max(0, Math.min(100, stat.pct))}%` }}
                    />
                  </span>
                  <span
                    className={cx(
                      'w-10 shrink-0 text-right text-xs tabular-nums',
                      isKey ? 'text-ok-ink' : isTopOffKey ? 'text-warn-ink' : 'text-ink-secondary'
                    )}
                  >
                    {stat.pct}%
                  </span>
                </li>
              );
            })}
          </ul>

          {/*
            A divergência — mais marcada ≠ gabarito — é o sinal que reforça a
            denúncia, e só ela ganha a família `warn`. Quando as duas coincidem
            a mesma linha continua ali, neutra: é informação, não alerta.
          */}
          {topChoice && (
            <p
              data-testid={divergent ? 'answer-divergence' : 'answer-agreement'}
              className={cx(
                'mt-3 flex items-start gap-2 rounded-chip border px-3 py-2 text-xs leading-snug',
                divergent
                  ? 'border-warn/40 bg-warn/[0.10] text-warn-ink'
                  : 'border-edge bg-surface-muted text-ink-secondary'
              )}
            >
              {divergent && <AlertTriangle size={14} className="mt-px shrink-0" />}
              <span>
                {divergent
                  ? `${topChoice.choice} é a mais marcada e o gabarito é ${answerKey} — a divergência reforça a denúncia.`
                  : `${topChoice.choice} é a mais marcada e é o gabarito atual.`}
              </span>
            </p>
          )}
        </section>
      </div>

      {/* ── Denúncias recebidas ─────────────────────────────────────────── */}
      <section className="rounded-card border border-edge bg-surface p-4">
        <h3 className={SECTION_TITLE}>
          Denúncias recebidas · {detail.reports.length}
        </h3>
        <ul className="mt-3 flex flex-col gap-3">
          {detail.reports.map((report) => (
            <li key={report.id} className="flex gap-3" data-testid="report-item">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-edge bg-surface-muted text-[11px] font-semibold text-ink-secondary">
                {initials(report.author.name)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-ink">{report.author.name}</span>
                  <span className="rounded-chip border border-warn/40 bg-warn/[0.10] px-2 py-0.5 text-[11px] font-medium text-warn-ink">
                    {REPORT_CATEGORY_LABELS[report.category]}
                  </span>
                  <span className="text-[11px] text-ink-muted">
                    {relativeTime(report.createdAt)}
                  </span>
                </div>
                <p className="mt-1 whitespace-pre-line text-sm leading-snug text-ink-secondary">
                  {report.description}
                </p>
                {report.reference && (
                  <a
                    href={report.reference}
                    target="_blank"
                    rel="noreferrer"
                    className={cx(
                      'mt-1.5 inline-flex max-w-full items-center gap-1.5 text-xs text-select-ink underline underline-offset-2',
                      'hover:brightness-110',
                      FOCUS
                    )}
                  >
                    <Link2 size={13} className="shrink-0" />
                    <span className="truncate">
                      {report.reference.replace(/^https?:\/\//, '')}
                    </span>
                    <ExternalLink size={12} className="shrink-0" />
                  </a>
                )}
              </div>
            </li>
          ))}
          {!detail.reports.length && (
            <li className="text-sm text-ink-muted">Nenhuma denúncia nesta questão.</li>
          )}
        </ul>
      </section>

      {/* ── Resposta e ações ────────────────────────────────────────────────
          `sticky bottom-0`: o detalhe é longo (enunciado + alternativas +
          distribuição + denúncias) e num viewport de 720px as decisões caíam
          duas telas abaixo — o admin não achava o botão de concluir. Pinado,
          o bloco de decisão fica visível enquanto o detalhe rola, o mesmo
          padrão da action bar do filtro. */}
      <section className="sticky bottom-0 z-10 rounded-card border border-edge bg-surface p-4 shadow-panel">
        <label htmlFor="report-response" className={SECTION_TITLE}>
          Resposta para quem denunciou
        </label>
        {/*
          O aviso antes do envio existe porque a tela não dizia o que a ação
          faz, e a leitura natural de um botão "Notificar os N alunos" num bloco
          de decisões é "isto encerra o caso". Não encerra: quem encerra é
          "Concluir: corrigida", "Rejeitar denúncia" ou a desativação.
        */}
        {canDecide && (
          <p className="mt-1 text-xs leading-snug text-ink-muted">
            Responder notifica quem denunciou e{' '}
            <strong className="font-semibold">não encerra a denúncia</strong>: o caso continua
            aberto até você concluir ou rejeitar.
          </p>
        )}
        {/*
          O mesmo campo serve às duas ações, com exigências diferentes — e a
          tela precisa dizer qual é qual. "Responder" sem texto não teria o que
          enviar; "Concluir: corrigida" tem o aviso padrão do servidor, então a
          mensagem é um acréscimo opcional.
        */}
        {canDecide && (
          <p className="mt-1 text-xs leading-snug text-ink-muted" data-testid="resolve-message-hint">
            Ao concluir como corrigida, o que estiver escrito aqui vai junto no aviso.{' '}
            <span className="font-semibold">A mensagem é opcional</span>: em branco, o aluno recebe
            o aviso padrão de questão corrigida.
          </p>
        )}
        <textarea
          id="report-response"
          rows={3}
          // 2000 runes é o limite do servidor nas duas rotas
          // (`reportModeratorMessageMaxRunes`); cortar aqui evita um 400 certo.
          maxLength={2000}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            setSent(false);
          }}
          placeholder="Explique o que foi verificado. Todos os denunciantes recebem a mesma resposta."
          className={cx(
            'mt-2 w-full resize-y rounded-input border border-edge bg-surface-muted px-3 py-2',
            'text-sm text-ink placeholder:text-ink-muted',
            FOCUS
          )}
        />

        {/* O estado lido depois do envio: respondida, e ainda por decidir. */}
        {sent && (
          <p
            className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ok-ink"
            role="status"
            data-testid="respond-result"
          >
            <span className="rounded-full border border-warn/40 bg-warn/[0.12] px-2 py-0.5 font-semibold text-warn-ink">
              Respondida · ainda aberta
            </span>
            Resposta enviada para {studentCountLabel(students)}.
          </p>
        )}

        {actionError && (
          <p className="mt-2 text-xs text-ink" role="alert" data-testid="report-action-error">
            {actionError}
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={cx(BTN, BTN_PRIMARY)}
            disabled={!message.trim() || busy !== null}
            onClick={respond}
          >
            <Send size={15} />
            {students === 1 ? 'Notificar o aluno' : `Notificar os ${students} alunos`}
          </button>

          {canDecide && (
            <button
              type="button"
              className={cx(BTN, BTN_NEUTRAL)}
              disabled={busy !== null}
              onClick={reject}
            >
              Rejeitar denúncia
            </button>
          )}

          {/*
            O desfecho que faltava: procedente, questão já corrigida. Fica ao
            lado de "Rejeitar denúncia" e de "Desativar questão" porque é irmão
            dos dois — e, como eles, só enquanto o caso está aberto.
          */}
          {canDecide && (
            <button
              type="button"
              className={cx(BTN, BTN_DECIDE)}
              disabled={busy !== null}
              onClick={resolve}
            >
              {busy === 'resolve' ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <CheckCircle2 size={15} />
              )}
              Concluir: corrigida
            </button>
          )}

          <button type="button" className={cx(BTN, BTN_NEUTRAL)} onClick={openEditor}>
            Corrigir gabarito
          </button>

          {/*
            `ml-auto` em vez de um <span className="flex-1" /> de enchimento: com
            cinco controles a linha quebra em telas estreitas, e o espaçador
            deixava "Desativar questão" encostada à esquerda da segunda linha.
            A margem automática mantém a ação destrutiva longe das outras em
            qualquer largura.
          */}
          {!detail.disabled && (
            <button
              type="button"
              className={cx(BTN, BTN_DANGER, 'ml-auto')}
              disabled={busy !== null}
              onClick={() => setConfirmOpen(true)}
            >
              <Ban size={15} />
              Desativar questão
            </button>
          )}
        </div>
      </section>

      {/* ── Confirmação da desativação (frame `cQlXD`) ──────────────────── */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="deactivate-title"
            className="w-full max-w-md rounded-modal border border-edge bg-surface p-5 shadow-panel"
          >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-card border border-danger/40 bg-danger/[0.12] text-danger-ink">
              <Ban size={20} />
            </span>

            <h2 id="deactivate-title" className="mt-3 text-base font-semibold text-ink">
              Desativar a questão #{questionNumber}?
            </h2>
            <p className="mt-1.5 text-sm leading-snug text-ink-secondary">
              Ela deixa de aparecer em treinos, simulados e revisões para todos os alunos. O
              histórico de respostas é mantido e você pode reativá-la depois.
            </p>

            <dl className="mt-4 divide-y divide-edge rounded-card border border-edge bg-surface-muted px-3">
              {[
                ['Motivo', category ? REPORT_CATEGORY_LABELS[category] : '—'],
                ['Denúncias', `${detail.reports.length} aceitas`],
                ['Notificação', studentCountLabel(students)],
              ].map(([term, value]) => (
                <div key={term} className="flex items-center justify-between gap-3 py-2">
                  <dt className="text-xs text-ink-muted">{term}</dt>
                  <dd className="text-xs font-medium text-ink">{value}</dd>
                </div>
              ))}
            </dl>

            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-ink-secondary">
              <input
                type="checkbox"
                checked={sendToContentTeam}
                onChange={(e) => setSendToContentTeam(e.target.checked)}
                className="h-4 w-4 accent-[rgb(var(--c-primary))]"
              />
              Enviar para a equipe de conteúdo revisar
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                ref={cancelRef}
                className={cx(BTN, BTN_NEUTRAL)}
                onClick={() => setConfirmOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={cx(BTN, BTN_DANGER)}
                disabled={busy === 'accept'}
                onClick={accept}
              >
                {busy === 'accept' ? <Loader2 size={15} className="animate-spin" /> : <Ban size={15} />}
                Desativar questão
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
